import { useEffect, useRef, type ElementType, type ReactNode } from "react";

type RevealProps = {
  children: ReactNode;
  as?: ElementType;
  className?: string;
  delay?: number;
  /** rootMargin bottom in px to trigger earlier/later */
  threshold?: number;
};

/**
 * IntersectionObserver-driven scroll reveal. Adds `in-view` once visible.
 * Pair with `.reveal` utility in styles.css.
 */
export function Reveal({
  children,
  as: Tag = "div",
  className = "",
  delay = 0,
  threshold = 0.12,
}: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      el.classList.add("in-view");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            if (delay) {
              window.setTimeout(() => el.classList.add("in-view"), delay);
            } else {
              el.classList.add("in-view");
            }
            io.unobserve(el);
          }
        });
      },
      { threshold, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [delay, threshold]);

  return (
    <Tag
      ref={ref as never}
      className={`reveal ${className}`}
      style={delay ? { transitionDelay: `${Math.min(delay, 600)}ms` } : undefined}
    >
      {children}
    </Tag>
  );
}

/**
 * Animated gradient blobs behind a section. Place inside a `relative` parent.
 */
export function GradientBackdrop({
  variant = "light",
  className = "",
}: {
  variant?: "light" | "dark";
  className?: string;
}) {
  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute inset-0 -z-10 overflow-hidden ${className}`}
    >
      <div
        className={`absolute -left-24 -top-24 h-[28rem] w-[28rem] rounded-full blur-3xl animate-blob ${
          variant === "dark"
            ? "bg-gold/20"
            : "bg-primary/10"
        }`}
      />
      <div
        className={`absolute -right-24 top-1/3 h-[26rem] w-[26rem] rounded-full blur-3xl animate-blob ${
          variant === "dark" ? "bg-secondary/30" : "bg-gold/15"
        }`}
        style={{ animationDelay: "-6s" }}
      />
      <div
        className={`absolute bottom-0 left-1/3 h-[24rem] w-[24rem] rounded-full blur-3xl animate-blob ${
          variant === "dark" ? "bg-primary/30" : "bg-secondary/10"
        }`}
        style={{ animationDelay: "-12s" }}
      />
    </div>
  );
}
